import React, { FunctionComponent, useCallback, useEffect, useMemo, useState } from 'react';
import { useLazyQuery } from '@apollo/client';
import { Button, Card, DatePicker, Spin } from 'antd';
import { DownloadOutlined, ReloadOutlined, FileTextOutlined, DollarOutlined, CheckCircleOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import ReactEcharts from 'echarts-for-react';
import classNames from 'classnames';
import { IAppComponentProps } from '../../../../../components';
import { getI18n } from '../../../../../utils/Utils';
import { QUERY_CFO_REPORTING } from './cfoReportingQueries';
import { EIM_THEME_STORAGE_KEY } from '../../../themePreference';
import styles from './cfoReportingDashboard.module.scss';
import { getRangePickerLocalization } from '../../../../info-portal/components/grid/CalendarUtils';
import { escapeHtml, getCountryFlagUrl } from '../../eimDashboardUtils';

const { RangePicker } = DatePicker;

type ThemeMode = 'dark' | 'light';

type CfoMetric = {
    label?: string | null;
    value?: number | null;
    delta?: number | null;
    percentageChange?: number | null;
};

type CfoTrendRow = {
    date?: string | null;
    invoiceCount?: number | null;
    previousInvoiceCount?: number | null;
    totalAmount?: number | null;
};

type CfoStatusRow = {
    status?: string | null;
    count?: number | null;
    percentage?: number | null;
};

type CfoCountryRow = {
    country?: string | null;
    invoiceCount?: number | null;
    percentage?: number | null;
};

type CfoCurrencyRow = {
    currency?: string | null;
    currentAmount?: number | null;
    previousAmount?: number | null;
    delta?: number | null;
    percentageChange?: number | null;
};

type CfoErrorReason = {
    reason?: string | null;
    count?: number | null;
    percentage?: number | null;
};

type CfoReportingData = {
    totalInvoices?: CfoMetric | null;
    totalAmount?: CfoMetric | null;
    successRate?: CfoMetric | null;
    errorRate?: CfoMetric | null;
    timeTrend?: CfoTrendRow[] | null;
    statusBreakdown?: CfoStatusRow[] | null;
    countryBreakdown?: CfoCountryRow[] | null;
    currencyBreakdown?: CfoCurrencyRow[] | null;
    topErrorReasons?: CfoErrorReason[] | null;
};

type CfoReportingQueryResult = {
    getCFOReporting?: CfoReportingData | null;
};

type CfoReportingCacheEntry = {
    updatedAt: number;
    snapshot: CfoReportingData | null;
};

type CfoDashboardViewType = 'reporting' | 'compliance';
type CfoDashboardVariant = 'default' | 'bmw-a1-performance';

type CfoDashboardProps = IAppComponentProps & {
    viewType?: CfoDashboardViewType;
    dashboardVariant?: CfoDashboardVariant;
    companyCode?: string;
};

const DEFAULT_RANGE_DAYS = 30;
const LEGACY_CFO_THEME_KEY = 'cfo-reporting-theme';
const LEGACY_CFO_COMPLIANCE_THEME_KEY = 'cfo-compliance-theme';
const CFO_REPORTING_CACHE_PREFIX = 'cfoReportingDashboardCache:v1';
const CFO_DASHBOARD_CACHE_TTL_MS = 10 * 60 * 1000;

const toDateRange = (daysBack: number): [Dayjs, Dayjs] => {
    const end = dayjs();
    return [end.subtract(daysBack - 1, 'day').startOf('day'), end.endOf('day')];
};

const formatDelta = (value?: number | null): string => {
    const delta = Number(value ?? 0);
    const sign = delta >= 0 ? '+' : '';
    return `${sign}${delta.toFixed(1)}`;
};

const formatPercent = (value?: number | null): string => `${Number(value ?? 0).toFixed(1)}%`;

const getComparisonPeriodLabel = (range: [Dayjs, Dayjs], intl: IAppComponentProps['intl']): string => {
    const rangeDays = range[1].startOf('day').diff(range[0].startOf('day'), 'day') + 1;
    if (rangeDays === 1) {
        return getI18n('cfo-reporting-period-yesterday', 'Yesterday', intl);
    }
    if (rangeDays >= 28 && rangeDays <= 31) {
        return getI18n('cfo-reporting-period-previous-month', 'Previous month', intl);
    }
    if (rangeDays >= 365 && rangeDays <= 366) {
        return getI18n('cfo-reporting-period-previous-year', 'Previous year', intl);
    }
    return getI18n('cfo-reporting-period-comparison-range', 'Comparison range', intl);
};

const formatCompactNumber = (value?: number | null): string => {
    const numberValue = Number(value ?? 0);
    if (Math.abs(numberValue) >= 1000000) {
        return `${(numberValue / 1000000).toFixed(2)}M`;
    }
    if (Math.abs(numberValue) >= 1000) {
        return `${(numberValue / 1000).toFixed(1)}K`;
    }
    return new Intl.NumberFormat().format(numberValue);
};

const formatMoney = (value?: number | null): string => {
    const numberValue = Number(value ?? 0);
    return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(numberValue);
};

const getStatusKey = (value?: string | null): string => String(value ?? '').trim().toLowerCase();

const getStatusColor = (value?: string | null): string => {
    const statusKey = getStatusKey(value);
    const statusColors: Record<string, string> = {
        delivered: '#22c55e',
        failed: '#ef4444',
        'in process': '#2563eb',
        'in transit': '#f59e0b',
        'business error': '#8b5cf6',
        overdue: '#64748b',
        unknown: '#94a3b8',
    };

    return statusColors[statusKey] ?? '#94a3b8';
};

const getStatusLegendClass = (value?: string | null): string => {
    const statusKey = getStatusKey(value);
    if (statusKey === 'delivered') {
        return styles.statusDelivered;
    }
    if (statusKey === 'failed') {
        return styles.statusFailed;
    }
    if (statusKey === 'in process') {
        return styles.statusInProcess;
    }
    if (statusKey === 'in transit') {
        return styles.statusInTransit;
    }
    if (statusKey === 'business error') {
        return styles.statusBusinessError;
    }
    if (statusKey === 'overdue') {
        return styles.statusOverdue;
    }
    return styles.statusUnknown;
};

const getCountryDisplayName = (value?: string | null, displayNames?: Intl.DisplayNames | null, unknownLabel = 'Unknown'): string => {
    const countryValue = String(value ?? '').trim();
    if (!countryValue) {
        return unknownLabel;
    }

    if (displayNames && /^[a-z]{2}$/i.test(countryValue)) {
        return displayNames.of(countryValue.toUpperCase()) ?? countryValue.toUpperCase();
    }

    return countryValue;
};

const getMetricIcon = (label: string) => {
    if (label.toLowerCase().includes('amount')) {
        return <DollarOutlined />;
    }
    if (label.toLowerCase().includes('success')) {
        return <CheckCircleOutlined />;
    }
    if (label.toLowerCase().includes('error')) {
        return <ExclamationCircleOutlined />;
    }
    return <FileTextOutlined />;
};

const CfoReportingDashboard: FunctionComponent<CfoDashboardProps> = (props) => {
    const intl = props.intl;
    const viewType = props.viewType ?? 'reporting';
    const dashboardVariant = props.dashboardVariant ?? 'default';
    const effectiveCompanyCode = props.companyCode ?? (dashboardVariant === 'bmw-a1-performance' ? 'A1' : undefined);
    const isBmwPerformanceDashboard = dashboardVariant === 'bmw-a1-performance';
    const isComplianceView = viewType === 'compliance';
    const legacyThemeStorageKey = isComplianceView ? LEGACY_CFO_COMPLIANCE_THEME_KEY : LEGACY_CFO_THEME_KEY;
    const [theme, setTheme] = useState<ThemeMode>(() => {
        const stored = globalThis.localStorage.getItem(EIM_THEME_STORAGE_KEY);
        if (stored === 'light' || stored === 'dark') {
            return stored;
        }

        const legacyStored = globalThis.localStorage.getItem(legacyThemeStorageKey);
        if (legacyStored === 'light' || legacyStored === 'dark') {
            return legacyStored;
        }

        return stored === 'light' ? 'light' : 'dark';
    });
    const [selectedRange, setSelectedRange] = useState<[Dayjs, Dayjs]>(() => toDateRange(DEFAULT_RANGE_DAYS));
    const [cachedReportingData, setCachedReportingData] = useState<CfoReportingData | null>(null);
    const [getCfoReporting, { loading, data }] = useLazyQuery<CfoReportingQueryResult>(QUERY_CFO_REPORTING);
    const comparisonPeriodLabel = getComparisonPeriodLabel(selectedRange, intl);

    const getCacheKey = useCallback((fromDate: Dayjs, toDate: Dayjs) => {
        const locale = globalThis.localStorage.getItem('locale') || 'en';
        const scopedCompanyCode = effectiveCompanyCode ?? 'all';
        return `${CFO_REPORTING_CACHE_PREFIX}:${viewType}:${dashboardVariant}:${scopedCompanyCode}:${locale}:${fromDate.format('YYYY-MM-DD')}:${toDate.format('YYYY-MM-DD')}`;
    }, [dashboardVariant, effectiveCompanyCode, viewType]);

    useEffect(() => {
        globalThis.localStorage.setItem(EIM_THEME_STORAGE_KEY, theme);
    }, [theme]);

    const loadReportingData = useCallback(async (forceRefresh = false) => {
        const [fromDate, toDate] = selectedRange;
        const cacheKey = getCacheKey(fromDate, toDate);

        if (forceRefresh) {
            globalThis.sessionStorage.removeItem(cacheKey);
        }

        if (!forceRefresh) {
            const cached = globalThis.sessionStorage.getItem(cacheKey);
            if (cached) {
                try {
                    const parsed = JSON.parse(cached) as CfoReportingCacheEntry;
                    const isFresh = Number.isFinite(parsed?.updatedAt)
                        && (Date.now() - parsed.updatedAt) <= CFO_DASHBOARD_CACHE_TTL_MS;
                    if (isFresh) {
                        setCachedReportingData(parsed.snapshot ?? null);
                        return;
                    }
                    globalThis.sessionStorage.removeItem(cacheKey);
                } catch {
                    globalThis.sessionStorage.removeItem(cacheKey);
                }
            }
        }

        const response = await getCfoReporting({
            variables: {
                fromDate: fromDate.format('YYYY-MM-DD'),
                toDate: toDate.format('YYYY-MM-DD'),
                companyCode: effectiveCompanyCode,
            },
            fetchPolicy: 'network-only',
        });

        const snapshot = response?.data?.getCFOReporting ?? null;
        setCachedReportingData(snapshot);
        const cacheEntry: CfoReportingCacheEntry = {
            updatedAt: Date.now(),
            snapshot,
        };
        globalThis.sessionStorage.setItem(cacheKey, JSON.stringify(cacheEntry));
    }, [effectiveCompanyCode, getCacheKey, getCfoReporting, selectedRange]);

    useEffect(() => {
        loadReportingData().catch(console.error);
    }, [loadReportingData]);

    const reportingData = data?.getCFOReporting ?? cachedReportingData ?? null;
    const kpis = [
        reportingData?.totalInvoices,
        reportingData?.successRate,
        reportingData?.errorRate,
    ];

    const trendSeries = useMemo(() => {
        const rows = reportingData?.timeTrend ?? [];
        return {
            labels: rows.map((row) => dayjs(row.date ?? undefined).format('MMM D')),
            invoiceCount: rows.map((row) => Number(row.invoiceCount ?? 0)),
            previousInvoiceCount: rows.map((row) => Number(row.previousInvoiceCount ?? 0)),
        };
    }, [reportingData?.timeTrend]);

    const statusRows = useMemo(() => (reportingData?.statusBreakdown ?? []), [reportingData?.statusBreakdown]);
    const countryRows = useMemo(() => (reportingData?.countryBreakdown ?? []).slice(0, 5), [reportingData?.countryBreakdown]);
    const currencyRows = useMemo(() => (reportingData?.currencyBreakdown ?? []), [reportingData?.currencyBreakdown]);
    const countryDisplayNames = useMemo(() => {
        if (!Intl.DisplayNames) {
            return null;
        }

        return new Intl.DisplayNames(['en'], { type: 'region' });
    }, []);
    const countryCodeRows = useMemo(
        () => countryRows.map((row) => ({
            name: getCountryDisplayName(row.country, countryDisplayNames, getI18n('cfo-reporting-dashboard-unknown-value', 'Unknown', intl)),
            invoiceCount: Number(row.invoiceCount ?? 0),
        })),
        [countryDisplayNames, countryRows]
    );

    const trendOption = useMemo(() => ({
        grid: { top: 30, right: 20, bottom: 30, left: 38 },
        tooltip: { trigger: 'axis' },
        xAxis: {
            type: 'category',
            data: trendSeries.labels,
            axisLine: { lineStyle: { color: theme === 'dark' ? '#475569' : '#cbd5e1' } },
            axisLabel: { color: theme === 'dark' ? '#cbd5e1' : '#334155' },
        },
        yAxis: {
            type: 'value',
            axisLabel: { color: theme === 'dark' ? '#cbd5e1' : '#334155' },
            splitLine: { lineStyle: { color: theme === 'dark' ? '#24324a' : '#e2e8f0' } },
        },
        series: [
            {
                name: getI18n('cfo-reporting-trend-invoices', 'Invoices', intl),
                type: 'line',
                smooth: true,
                symbolSize: 6,
                lineStyle: { color: '#2563eb', width: 3 },
                itemStyle: { color: '#2563eb' },
                areaStyle: { color: 'rgba(37, 99, 235, 0.12)' },
                data: trendSeries.invoiceCount,
            },
            {
                name: comparisonPeriodLabel,
                type: 'line',
                smooth: true,
                symbolSize: 5,
                lineStyle: { color: theme === 'dark' ? '#cbd5e1' : '#64748b', width: 2, type: 'dashed' },
                itemStyle: { color: theme === 'dark' ? '#cbd5e1' : '#64748b' },
                data: trendSeries.previousInvoiceCount,
            },
        ],
    }), [comparisonPeriodLabel, intl, theme, trendSeries]);

    const statusOption = useMemo(() => ({
        series: [{
            type: 'pie',
            radius: ['58%', '78%'],
            center: ['50%', '52%'],
            avoidLabelOverlap: false,
            label: { show: false },
            data: statusRows.map((row, index) => ({
                value: Number(row.count ?? 0),
                name: row.status ?? getI18n('cfo-reporting-status-unknown', 'Unknown', intl),
                itemStyle: {
                    color: getStatusColor(row.status),
                },
            })),
        }],
    }), [intl, statusRows]);

    const countryOption = useMemo(() => ({
        grid: { top: 10, right: 30, bottom: 10, left: 100 },
        tooltip: {
            trigger: 'axis',
            axisPointer: { type: 'shadow' },
            formatter: (params: any) => {
                const rowIndex = Array.isArray(params) ? params[0]?.dataIndex : params?.dataIndex;
                const row = countryRows[rowIndex] ?? null;
                if (!row) {
                    return '';
                }

                const countryName = getCountryDisplayName(row.country, countryDisplayNames, getI18n('cfo-reporting-dashboard-unknown-value', 'Unknown', intl));
                const flagSrc = getCountryFlagUrl(row.country);
                const value = Number(row.invoiceCount ?? 0);
                const percent = Number(row.percentage ?? 0);
                const flagMarkup = flagSrc
                    ? `<img src="${flagSrc}" alt="${escapeHtml(countryName)}" width="20" height="20" style="display:block;height:20px;object-fit:cover;width:20px;" onerror="this.style.display='none';" />`
                    : '';

                return `
                    <div style="align-items:center;display:flex;gap:8px;font-weight:700;margin-bottom:6px;">
                        <span style="align-items:center;background:#fff;border:1px solid rgba(148,163,184,0.35);border-radius:999px;display:inline-flex;height:20px;justify-content:center;overflow:hidden;width:20px;">${flagMarkup}</span>
                        <span>${escapeHtml(countryName)}</span>
                    </div>
                    <div>${escapeHtml(getI18n('cfo-reporting-country-tooltip-invoices', 'Invoices', intl))}: ${formatCompactNumber(value)}</div>
                    <div>${escapeHtml(getI18n('cfo-reporting-country-tooltip-share', 'Share', intl))}: ${formatPercent(percent)}</div>
                `;
            },
        },
        xAxis: {
            type: 'value',
            axisLabel: { show: false },
            splitLine: { show: false },
        },
        yAxis: {
            type: 'category',
            data: countryCodeRows.map((row) => row.name),
            axisLabel: {
                color: theme === 'dark' ? '#cbd5e1' : '#0f172a',
                fontWeight: 700,
            },
            axisLine: { show: false },
            axisTick: { show: false },
        },
        series: [{
            type: 'bar',
            data: countryCodeRows.map((row) => row.invoiceCount),
            barWidth: 14,
            itemStyle: { color: '#2563eb', borderRadius: [0, 999, 999, 0] },
            label: {
                show: true,
                position: 'right',
                color: theme === 'dark' ? '#e2e8f0' : '#0f172a',
                fontWeight: 700,
                formatter: ({ value }: { value: number }) => `${Number(value ?? 0).toFixed(0)}`,
            },
        }],
    }), [countryCodeRows, countryDisplayNames, countryRows, theme]);

    const setPresetRange = (daysBack: number) => {
        setSelectedRange(toDateRange(daysBack));
    };

    const handleRefresh = useCallback(() => {
        loadReportingData(true).catch(console.error);
    }, [loadReportingData]);

    const exportCurrentView = () => {
        const payload = {
            range: selectedRange.map((value) => value.format('YYYY-MM-DD')),
            data: reportingData,
        };
        const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
        const link = globalThis.document.createElement('a');
        link.href = globalThis.URL.createObjectURL(blob);
        link.download = isComplianceView ? 'cfo-compliance-report.json' : 'cfo-reporting.json';
        link.click();
        globalThis.URL.revokeObjectURL(link.href);
    };

    let heroEyebrow = getI18n('cfo-reporting-eyebrow', 'Financial Overview', intl);
    let heroTitle = getI18n('cfo-reporting-title', 'CFO Reporting', intl);
    let heroSubtitle = getI18n('cfo-reporting-subtitle', 'A financial view of invoice volume, amount, delivery health, and rejection trends.', intl);

    if (isComplianceView) {
        heroEyebrow = getI18n('cfo-compliance-eyebrow', 'Compliance Overview', intl);
        heroTitle = getI18n('cfo-compliance-title', 'CFO Compliance Report', intl);
        heroSubtitle = getI18n('cfo-compliance-subtitle', 'A compliance-focused view of invoice delivery quality, rejection risk, and country-level behavior.', intl);
    }

    if (isBmwPerformanceDashboard) {
        heroEyebrow = getI18n('bmw-einvoicing-performance-dashboard-eyebrow', 'BMW A1 Overview', intl);
        heroTitle = getI18n('bmw-einvoicing-performance-dashboard-title', 'BMW eInvoicing Performance Dashboard', intl);
        heroSubtitle = getI18n('bmw-einvoicing-performance-dashboard-subtitle', 'A1-only view of invoice volume, amount, delivery health, and rejection trends.', intl);
    }

    return (
        <Spin spinning={loading} size="large">
            <div className={classNames(styles.page, {
                [styles.pageLight]: theme === 'light',
                [styles.pageDark]: theme === 'dark',
            })}>
                <div className={styles.shell}>
                    <div className={styles.toolbar}>
                        <Button icon={<ReloadOutlined />} onClick={handleRefresh}>
                            {getI18n('cfo-reporting-refresh', 'Refresh', intl)}
                        </Button>
                        <Button icon={<DownloadOutlined />} type="primary" onClick={exportCurrentView}>
                            {getI18n('cfo-reporting-export', 'Export', intl)}
                        </Button>
                        <Button onClick={() => setTheme((previous) => (previous === 'dark' ? 'light' : 'dark'))}>
                            {theme === 'dark' ? getI18n('cfo-reporting-switch-light', 'Switch to light view', intl) : getI18n('cfo-reporting-switch-dark', 'Switch to dark view', intl)}
                        </Button>
                    </div>

                    <div className={styles.heroCard}>
                        <div>
                            <span className={styles.eyebrow}>{heroEyebrow}</span>
                            <h1 className={styles.heroTitle}>{heroTitle}</h1>
                            <p className={styles.heroLead}>{heroSubtitle}</p>
                        </div>
                        <div className={styles.heroMeta}>
                            <div className={styles.metaPill}>
                                <span className={styles.metaLabel}>{getI18n('cfo-reporting-period', 'Period', intl)}</span>
                                <RangePicker
                                    value={selectedRange}
                                    onChange={(values) => {
                                        if (values?.[0] && values?.[1]) {
                                            setSelectedRange([values[0], values[1]]);
                                        }
                                    }}
                                    allowClear={false}
                                    format="MMM D, YYYY"
                                    locale={getRangePickerLocalization(intl, globalThis.localStorage.getItem('locale') || 'en')}
                                />
                            </div>
                            <div className={styles.quickFilters}>
                                <Button onClick={() => setPresetRange(1)}>{getI18n('cfo-reporting-preset-today', 'Today', intl)}</Button>
                                <Button onClick={() => setPresetRange(30)}>{getI18n('cfo-reporting-preset-month', 'This Month', intl)}</Button>
                                <Button onClick={() => setPresetRange(365)}>{getI18n('cfo-reporting-preset-year', 'This Year', intl)}</Button>
                            </div>
                        </div>
                    </div>

                    <div className={styles.kpiGrid}>
                        {kpis.map((metric, index) => {
                            const metricLabel = metric?.label ?? '';
                            const delta = metric?.percentageChange ?? 0;
                            const metricLabelLower = metricLabel.toLowerCase();
                            const isRateMetric = metricLabelLower.includes('rate');
                            const positive = isRateMetric ? delta <= 0 : delta >= 0;
                            let metricValueDisplay = formatCompactNumber(metric?.value);
                            if (isRateMetric) {
                                metricValueDisplay = `${formatCompactNumber(metric?.value)}%`;
                            }
                            return (
                                <Card key={metricLabel || index} className={styles.kpiCard} bordered={false}>
                                    <div className={styles.kpiHeader}>
                                        <div>
                                            <div className={styles.kpiLabel}>{metricLabel || getI18n('cfo-reporting-metric', 'Metric', intl)}</div>
                                            <div className={styles.kpiValue}>{metricValueDisplay}</div>
                                        </div>
                                        <div className={classNames(styles.kpiIcon, { [styles.kpiIconPositive]: !isRateMetric, [styles.kpiIconNegative]: isRateMetric })}>{getMetricIcon(metricLabel)}</div>
                                    </div>
                                    <div className={classNames(styles.kpiTrend, positive ? styles.kpiTrendPositive : styles.kpiTrendNegative)}>
                                        {positive ? '▲' : '▼'} {formatDelta(metric?.percentageChange)}% {getI18n('cfo-reporting-vs-comparison', 'vs. comparison range', intl)}
                                    </div>
                                </Card>
                            );
                        })}
                    </div>

                    <div className={styles.mainGrid}>
                        <Card className={styles.chartCard} bordered={false}>
                            <div className={styles.cardTitle}>{getI18n('cfo-reporting-trend-title', 'Invoices Over Time', intl)}</div>
                            <ReactEcharts className={styles.trendChart} option={trendOption} />
                        </Card>
                        <Card className={styles.chartCard} bordered={false}>
                            <div className={styles.cardTitle}>{getI18n('cfo-reporting-status-title', 'Invoices by Status', intl)}</div>
                            <div className={styles.statusPanel}>
                                <ReactEcharts className={styles.statusChart} option={statusOption} />
                                <div className={styles.statusLegend}>
                                    {statusRows.map((row, index) => (
                                        <div className={styles.legendRow} key={`${row.status}-${index}`}>
                                            <span className={classNames(styles.legendDot, getStatusLegendClass(row.status))} />
                                            <span>{row.status ?? getI18n('cfo-reporting-status-unknown', 'Unknown', intl)}</span>
                                            <strong>{formatPercent(row.percentage)}</strong>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </Card>
                    </div>

                    <div className={styles.bottomGrid}>
                        <Card className={styles.chartCard} bordered={false}>
                            <div className={styles.cardTitle}>{getI18n('cfo-reporting-country-title', 'Invoicing by Country (Top 5)', intl)}</div>
                            <ReactEcharts className={styles.countryChart} option={countryOption} />
                        </Card>
                        <Card className={styles.chartCard} bordered={false}>
                            <div className={styles.cardTitle}>{getI18n('cfo-reporting-currency-breakdown-title', 'Total Amount by Currency', intl)}</div>
                            <div className={styles.currencyTableWrapper}>
                                <table className={styles.currencyTable}>
                                    <thead>
                                        <tr>
                                            <th>{getI18n('cfo-reporting-currency-col-currency', 'Currency', intl)}</th>
                                            <th>{getI18n('cfo-reporting-currency-col-current', 'Current Period', intl)}</th>
                                            <th>{comparisonPeriodLabel}</th>
                                            <th>{getI18n('cfo-reporting-currency-col-delta', 'Change', intl)}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {currencyRows.length > 0 ? currencyRows.map((row, index) => {
                                            const delta = Number(row.delta ?? 0);
                                            const isPositive = delta >= 0;

                                            return (
                                                <tr key={`${row.currency ?? 'UNKNOWN'}-${index}`}>
                                                    <td>{row.currency ?? getI18n('cfo-reporting-dashboard-unknown-value', 'Unknown', intl)}</td>
                                                    <td>{formatMoney(row.currentAmount)}</td>
                                                    <td>{formatMoney(row.previousAmount)}</td>
                                                    <td className={classNames(styles.currencyDelta, isPositive ? styles.currencyDeltaPositive : styles.currencyDeltaNegative)}>
                                                        {isPositive ? '+' : ''}{formatMoney(delta)}
                                                    </td>
                                                </tr>
                                            );
                                        }) : (
                                            <tr>
                                                <td colSpan={4} className={styles.currencyEmpty}>
                                                    {getI18n('cfo-reporting-currency-empty', 'No currency amount data for this period.', intl)}
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </Card>
                    </div>
                </div>
            </div>
        </Spin>
    );
};

export default CfoReportingDashboard;
